import { useState } from 'react';
import { Send, RotateCcw, Loader2 } from 'lucide-react';

interface DeliveryControlsProps {
  letterStatus: string;
  onQueueNow: (reason: string) => Promise<void>;
  onResend: (reason: string) => Promise<void>;
}

export function DeliveryControls({ letterStatus, onQueueNow, onResend }: DeliveryControlsProps) {
  const [queueReason, setQueueReason] = useState('Testing: queue for now');
  const [resendReason, setResendReason] = useState('');
  const [isQueueing, setIsQueueing] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleQueueNow = async () => {
    if (!queueReason.trim()) return;
    setIsQueueing(true);
    try {
      await onQueueNow(queueReason.trim());
      setQueueReason('Testing: queue for now');
    } finally {
      setIsQueueing(false);
    }
  };

  const handleResend = async () => {
    if (!resendReason.trim()) return;
    setIsResending(true);
    try {
      await onResend(resendReason.trim());
      setResendReason('');
    } finally {
      setIsResending(false);
    }
  };

  const showQueueNow = letterStatus !== 'sent';
  const showResend = letterStatus === 'failed';

  if (!showQueueNow && !showResend) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Queue Now */}
      {showQueueNow && (
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-medium text-parchment">Queue for Immediate Send</h4>
            <p className="text-xs text-mist mt-1">
              Queue this letter for immediate testing delivery. It will bypass the scheduled window
              and be sent as soon as the send queue picks it up.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              value={queueReason}
              onChange={(e) => setQueueReason(e.target.value)}
              placeholder="Reason for immediate send..."
              className="flex-1 px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
            />
            <button
              disabled={isQueueing || !queueReason.trim()}
              onClick={handleQueueNow}
              className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-rune text-night hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isQueueing ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Send size={14} />
              )}
              {isQueueing ? 'Queueing...' : 'Queue Now'}
            </button>
          </div>
        </div>
      )}

      {/* Resend (for failed letters) */}
      {showResend && (
        <div className="space-y-3 pt-4 border-t border-stone/20">
          <div>
            <h4 className="text-sm font-medium text-parchment">Resend Letter</h4>
            <p className="text-xs text-mist mt-1">
              Queue this letter for a full resend attempt. This will regenerate the letter and
              attempt delivery again.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              value={resendReason}
              onChange={(e) => setResendReason(e.target.value)}
              placeholder="Reason for resend..."
              className="flex-1 px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
            />
            <button
              disabled={isResending || !resendReason.trim()}
              onClick={handleResend}
              className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border border-rune/30 bg-rune/20 text-rune hover:bg-rune/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isResending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <RotateCcw size={14} />
              )}
              {isResending ? 'Queueing...' : 'Resend'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
